import React, { useEffect } from "react";
import { Navigate, useParams } from "react-router-dom";
import styled from "styled-components";

import { Nav } from "../components/Nav";
import Footer from "../components/Footer";
import { Icon } from "../components/Icon";
import FormularioDiagnostico from "../components/FormularioDiagnostico";
import { DIAGNOSTICO, CLIENTES, waLink } from "../data/diagnostico";
import {
    SITE_URL,
    absoluteUrl,
    setMetaTag,
    setLinkTag,
    setStructuredData,
    removeStructuredData,
    restoreDefaultMeta
} from "../utils/pageMeta";

const Page = styled.main`
    width: 100%;
    padding: 148px 0 0;
    overflow-x: hidden;

    @media only screen and (max-width: 720px) {
        padding-top: 118px;
    }
`;

const Section = styled.section`
    width: min(1180px, calc(100vw - 32px));
    margin: 0 auto;

    @media only screen and (max-width: 640px) {
        width: min(100%, calc(100vw - 24px));
    }
`;

const Breadcrumb = styled.nav`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 700;
    color: var(--muted-soft);

    a { color: var(--brand); }

    .material-symbols-rounded { font-size: 16px; }
`;

const Hero = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
    gap: 40px;
    align-items: start;
    padding: 16px 0 40px;

    @media only screen and (max-width: 860px) {
        grid-template-columns: 1fr;
        gap: 24px;
    }
`;

const HeroCopy = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const Eyebrow = styled.span`
    font-size: 12.5px;
    font-weight: 800;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--brand);
`;

const Title = styled.h1`
    margin: 0;
    font-size: clamp(2rem, 4vw, 2.8rem);
    font-weight: 800;
    line-height: 1.1;
    letter-spacing: -0.025em;
    color: var(--ink);
    max-width: 22ch;
    text-wrap: balance;
`;

const Lead = styled.p`
    margin: 0;
    max-width: 56ch;
    font-size: 1.08rem;
    font-weight: 500;
    line-height: 1.62;
    color: var(--muted);
`;

const SignalsCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 24px;
    border-radius: 18px;
    background: var(--bg-soft);
    border: 1px solid var(--line);
`;

const SignalsTitle = styled.span`
    font-size: 12.5px;
    font-weight: 800;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--warning);
`;

const SignalRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;

    .material-symbols-rounded { color: var(--warning); margin-top: 2px; }
`;

const SignalText = styled.span`
    font-size: 14.5px;
    font-weight: 600;
    line-height: 1.5;
    color: var(--ink);
`;

const TwoCol = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.85fr);
    gap: 40px;
    align-items: start;
    padding: 24px 0 56px;
    border-top: 1px solid var(--line);

    @media only screen and (max-width: 860px) {
        grid-template-columns: 1fr;
        gap: 24px;
    }
`;

const ColTitle = styled.h2`
    margin: 0 0 14px;
    font-size: 1.35rem;
    font-weight: 800;
    color: var(--ink);
`;

const EntregasList = styled.div`
    display: grid;
    gap: 10px;
`;

const EntregaRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 11px;
    padding: 14px 16px;
    border-radius: 12px;
    background: #fff;
    border: 1px solid var(--line);

    .material-symbols-rounded { color: var(--success); margin-top: 1px; }
`;

const EntregaText = styled.span`
    font-size: 15px;
    font-weight: 600;
    line-height: 1.5;
    color: var(--ink);
`;

const CaseCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 28px;
    border-radius: 18px;
    background: var(--ink);
    color: #fff;
`;

const CaseClient = styled.span`
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--brand-3);
`;

const CaseMetric = styled.strong`
    display: block;
    font-size: 38px;
    font-weight: 800;
    line-height: 1;
    letter-spacing: -0.03em;
    color: var(--brand-3);
`;

const CaseMetricLabel = styled.span`
    display: block;
    margin-top: 4px;
    font-size: 14.5px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.86);
`;

const CaseQuote = styled.p`
    margin: 0;
    font-family: var(--font-quote);
    font-style: italic;
    font-size: 18px;
    line-height: 1.5;
    color: #fff;
`;

const CaseAuthor = styled.span`
    font-size: 13px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.78);
`;

const NoCaseCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 28px;
    border-radius: 18px;
    background: var(--ink);
    color: #fff;
`;

const NoCaseLabel = styled.span`
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--brand-3);
`;

const NoCaseText = styled.p`
    margin: 0;
    font-size: 16.5px;
    font-weight: 600;
    line-height: 1.55;
    color: #fff;
`;

const ExemploList = styled.ol`
    margin: 0;
    padding-left: 18px;
    display: grid;
    gap: 8px;
    font-size: 14.5px;
    font-weight: 500;
    line-height: 1.55;
    color: rgba(255, 255, 255, 0.86);
`;

const ClienteChips = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding-top: 14px;
    border-top: 1px solid rgba(255, 255, 255, 0.16);
`;

const ClienteChip = styled.span`
    display: inline-flex;
    align-items: center;
    min-height: 30px;
    padding: 0 12px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.18);
    font-size: 13px;
    font-weight: 700;
    color: #fff;
`;

const FaqSection = styled.div`
    padding: 0 0 56px;
    border-top: 1px solid var(--line);
    padding-top: 40px;
    display: grid;
    gap: 10px;
    max-width: 760px;
`;

const FaqItem = styled.details`
    border-radius: 12px;
    background: var(--bg-soft);
    border: 1px solid var(--line);

    summary {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        padding: 18px 20px;
        cursor: pointer;
        list-style: none;
        font-size: 15.5px;
        font-weight: 800;
        color: var(--ink);
    }

    summary::-webkit-details-marker { display: none; }
    .material-symbols-rounded { color: var(--brand); flex: none; }
`;

const FaqAnswer = styled.p`
    margin: 0;
    padding: 0 20px 18px;
    font-size: 14.5px;
    font-weight: 500;
    line-height: 1.6;
    color: var(--muted);
`;

const FormSection = styled.div`
    background: var(--ink);
`;

const FormGrid = styled(Section)`
    padding: 56px 0;
    display: grid;
    grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
    gap: 48px;
    align-items: start;

    @media only screen and (max-width: 860px) {
        grid-template-columns: 1fr;
        gap: 28px;
    }
`;

const FormTitle = styled.h2`
    margin: 0 0 12px;
    font-size: clamp(1.6rem, 2.8vw, 2rem);
    font-weight: 800;
    color: #fff;
`;

const FormLead = styled.p`
    margin: 0;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.78);
`;

const OutrasSection = styled(Section)`
    padding: 40px 0 72px;
`;

const OutrasTitle = styled.h2`
    margin: 0 0 14px;
    font-size: 1.2rem;
    font-weight: 800;
    color: var(--ink);
`;

const OutrasGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
`;

const OutraLink = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-height: 42px;
    padding: 0 14px;
    border-radius: 12px;
    border: 1.5px solid var(--line);
    background: #fff;
    color: var(--ink);
    font-size: 14px;
    font-weight: 700;

    &:hover { border-color: #c9bdea; color: var(--brand-2); }
`;

function DiagnosticRoute() {
    const { slug } = useParams();
    const item = DIAGNOSTICO.find((entry) => entry.slug === slug);

    useEffect(() => {
        if (!item) return undefined;

        const pageUrl = `${SITE_URL}${item.route}`;
        const pageTitle = `${item.tag} · Mango Software House`;

        document.title = pageTitle;
        setMetaTag('meta[name="description"]', { name: "description", content: item.body });
        setMetaTag('meta[property="og:title"]', { property: "og:title", content: pageTitle });
        setMetaTag('meta[property="og:description"]', { property: "og:description", content: item.body });
        setMetaTag('meta[property="og:url"]', { property: "og:url", content: pageUrl });
        setLinkTag('link[rel="canonical"]', { rel: "canonical", href: pageUrl });

        setStructuredData("route-schema", {
            "@context": "https://schema.org",
            "@graph": [
                {
                    "@type": "FAQPage",
                    mainEntity: item.faq.map((q) => ({
                        "@type": "Question",
                        name: q.q,
                        acceptedAnswer: { "@type": "Answer", text: q.a }
                    }))
                },
                {
                    "@type": "BreadcrumbList",
                    itemListElement: [
                        { "@type": "ListItem", position: 1, name: "Início", item: SITE_URL },
                        { "@type": "ListItem", position: 2, name: "Diagnóstico", item: absoluteUrl("/diagnostico") },
                        { "@type": "ListItem", position: 3, name: item.label, item: pageUrl }
                    ]
                },
                {
                    "@type": "Service",
                    name: item.tag,
                    serviceType: item.tag,
                    areaServed: ["BR", "PT"],
                    provider: {
                        "@type": "Organization",
                        name: "Mango Software House",
                        telephone: "+554191150056",
                        email: "contato@mangosoftwarehouse.com"
                    },
                    description: item.body
                }
            ]
        });

        return () => {
            removeStructuredData("route-schema");
            restoreDefaultMeta();
        };
    }, [item]);

    if (!item) {
        return <Navigate to="/" replace />;
    }

    const outras = DIAGNOSTICO.filter((entry) => entry.id !== item.id);

    return (
        <>
            <Nav />

            <Page id="top">
                <Section>
                    <Breadcrumb aria-label="Trilha de navegação">
                        <a href="/">Início</a>
                        <Icon name="chevron_right" />
                        <span>{item.label}</span>
                    </Breadcrumb>

                    <Hero>
                        <HeroCopy>
                            <Eyebrow>{item.tag}</Eyebrow>
                            <Title>{item.title}</Title>
                            <Lead>{item.body}</Lead>
                        </HeroCopy>

                        <SignalsCard>
                            <SignalsTitle>Sinais de que é isso</SignalsTitle>
                            {item.sinais.map((sinal) => (
                                <SignalRow key={sinal}>
                                    <Icon name="priority_high" size="18px" />
                                    <SignalText>{sinal}</SignalText>
                                </SignalRow>
                            ))}
                        </SignalsCard>
                    </Hero>

                    <TwoCol>
                        <div>
                            <ColTitle>O que é entregue</ColTitle>
                            <EntregasList>
                                {item.entregas.map((entrega) => (
                                    <EntregaRow key={entrega}>
                                        <Icon name="check_circle" size="19px" />
                                        <EntregaText>{entrega}</EntregaText>
                                    </EntregaRow>
                                ))}
                            </EntregasList>
                        </div>

                        {!item.noCase && (
                            <CaseCard>
                                <CaseClient>{item.client}</CaseClient>
                                <div>
                                    <CaseMetric>{item.metric}</CaseMetric>
                                    <CaseMetricLabel>{item.metricLabel}</CaseMetricLabel>
                                </div>
                                <CaseQuote>“{item.quote}”</CaseQuote>
                                <CaseAuthor>{item.author}</CaseAuthor>
                            </CaseCard>
                        )}

                        {item.noCase && item.semCategoria && (
                            <NoCaseCard>
                                <NoCaseLabel>Não precisa acertar a categoria</NoCaseLabel>
                                <NoCaseText>A maioria dos projetos começa com o cliente descrevendo a rotina em duas frases — a classificação é trabalho nosso, não seu.</NoCaseText>
                                <ClienteChips>
                                    {CLIENTES.map((cliente) => <ClienteChip key={cliente}>{cliente}</ClienteChip>)}
                                </ClienteChips>
                            </NoCaseCard>
                        )}

                        {item.noCase && !item.semCategoria && item.exemploSemCliente && (
                            <NoCaseCard>
                                <NoCaseLabel>Como costuma ser na prática</NoCaseLabel>
                                <ExemploList>
                                    {item.exemploSemCliente.map((linha) => <li key={linha}>{linha}</li>)}
                                </ExemploList>
                            </NoCaseCard>
                        )}
                    </TwoCol>

                    <FaqSection>
                        <ColTitle>Perguntas sobre {item.tag.toLowerCase()}</ColTitle>
                        {item.faq.map((q) => (
                            <FaqItem key={q.q}>
                                <summary>{q.q}<Icon name="expand_more" size="20px" /></summary>
                                <FaqAnswer>{q.a}</FaqAnswer>
                            </FaqItem>
                        ))}
                    </FaqSection>
                </Section>

                <FormSection id="form">
                    <FormGrid>
                        <div>
                            <FormTitle>{item.fechamento}</FormTitle>
                            <FormLead>A situação já vem marcada. Faltam três campos.</FormLead>
                        </div>
                        <FormularioDiagnostico
                            situacao={item.tag}
                            exemplo={item.placeholder}
                            permitirCurto={item.id === "outro"}
                            trocarHref="/#diagnostico"
                        />
                    </FormGrid>
                </FormSection>

                <OutrasSection>
                    <OutrasTitle>Não é bem isso? Veja as outras situações</OutrasTitle>
                    <OutrasGrid>
                        {outras.map((entry) => (
                            <OutraLink key={entry.id} href={entry.route}>
                                <Icon name={entry.icon} size="18px" />
                                {entry.label}
                            </OutraLink>
                        ))}
                        <OutraLink href={waLink(`Olá! Vim pela página ${item.route}. Minha situação: ${item.label}.`)} target="_blank" rel="noreferrer">
                            <Icon name="chat" size="18px" />
                            Falar no WhatsApp
                        </OutraLink>
                    </OutrasGrid>
                </OutrasSection>
            </Page>

            <Footer />
        </>
    );
}

export { DiagnosticRoute };
export default DiagnosticRoute;
